/* eslint-disable ember/no-classic-components, ember/no-computed-properties-in-native-classes */
import Component from '@ember/component';
import { action, computed } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { tagName } from '@ember-decorators/component';
import { cellStatus, percentOf } from 'linced-app/utils/progress-overview';

/**
 * Insights::StrandBands is the "By strand" table: each strand's descriptor
 * spread for the chosen half, plus on-track counts for both halves.
 *
 * @param {{year: string, rows: Array<Object>}} strandData - the progress
 *   overview's strand breakdown for the selected year.
 * @param {string} selectedYear - the page's chosen year.
 * @param {Array<{key, label, swatchStyle}>} levels - the progress levels,
 *   as the progress overview controller builds them.
 * @param {{mid: string, end: string}} labels - what the two slots of the
 *   year are called in the chart's mode.
 * @param {{interim: number, full: number}} onTrackGrades
 * @param {number} learnerCount - how many students the page covers.
 * @param {string} exportTitle
 * @param {Function} onSelect - called with (year, strand, half, judgement); a
 *   null judgement means everyone on track.
 */
@tagName('')
export default class StrandBands extends Component {
  /** @type {{year: string, rows: Array<Object>}|null} */
  strandData = null;

  /** @type {string|null} */
  selectedYear = null;

  /** @type {Ember.Array|null} */
  levels = null;

  /** @type {{interim: number, full: number}|null} */
  onTrackGrades = null;

  /** @type {{mid: string, end: string}|null} */
  labels = null;

  /** @type {number} */
  learnerCount = 0;

  /** @type {string} */
  exportTitle = '';

  /** @type {Function} */
  onSelect() {}

  /** @type {string} Which slot the descriptor columns show. */
  @tracked half = 'end';

  @computed('labels.{mid,end}')
  get halfOptions() {
    return [
      { value: 'mid', label: this.labels?.mid },
      { value: 'end', label: this.labels?.end },
    ];
  }

  @computed(
    'half',
    'labels.{mid,end}',
    'levels',
    'onTrackGrades.{full,interim}',
    'strandData.{rows,year}',
  )
  get bands() {
    let half = this.half;
    // Lowest descriptor first, so the columns read left to right the way the
    // levels do, matching the bars elsewhere on the page.
    let descriptors = [...(this.levels || [])]
      .sort((a, b) => a.key - b.key)
      .map((level) => ({
        judgement: level.key,
        label: level.label,
        swatchStyle: level.swatchStyle,
      }));
    // empty marks a half with no judgements for the strand, which reads as
    // "—" rather than a count of zero out of nobody.
    let figure = (count, total) => ({
      count,
      pct: total ? percentOf(count, total) : null,
      empty: !total,
    });

    let rows = (this.strandData?.rows || []).map((row) => {
      let counts = row[`${half}Counts`];
      let onTrack = row[`${half}CellOnTrack`];
      let total = row[`${half}Total`];
      return {
        key: row.key,
        label: row.label,
        cells: descriptors.map((descriptor) => {
          let index = descriptor.judgement - 1;
          return Object.assign(
            {
              judgement: descriptor.judgement,
              // Highlighted the way the count tables highlight their cells.
              status: cellStatus(
                counts[index],
                onTrack[index],
                descriptor.judgement,
                half,
                this.onTrackGrades,
              ),
            },
            figure(counts[index], total),
          );
        }),
        midOnTrack: figure(row.midOnTrack, row.midTotal),
        endOnTrack: figure(row.endOnTrack, row.endTotal),
      };
    });

    return {
      year: this.strandData?.year,
      halfLabel: this.labels?.[half],
      descriptors,
      rows,
    };
  }

  @action
  setHalf(half) {
    this.half = half;
  }
}
